import React from 'react';
import {
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { AspectosAMejorar as AspectosAMejorarType } from '../../../types/consolidatedReport.types';

interface AspectosAMejorarProps {
  data: AspectosAMejorarType;
}

const scoreColor = (score: number) => {
  if (score >= 80) return 'success'   as const;
  if (score >= 60) return 'warning'   as const;
  return           'error'            as const;
};

const AspectosAMejorar: React.FC<AspectosAMejorarProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const allCategorias = [
    ...data.categorias_criticas,
    ...data.categorias_riesgo,
  ];

  const isEmpty =
    allCategorias.length === 0 && data.top_preguntas_peores.length === 0;

  return (
    <Paper
      elevation={2}
      sx={{ p: isMobile ? 2 : 3, mb: 3, width: '100%', overflow: 'hidden' }}
    >
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        gutterBottom
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <ErrorIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
        Aspectos a Mejorar
      </Typography>

      {isEmpty ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          ¡Excelente! No se identificaron aspectos críticos en este periodo.
        </Typography>
      ) : (
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Categorías con bajo puntaje en alguna auditoría */}
          {allCategorias.length > 0 && (
            <Grid item xs={12} md={6}>
              {data.categorias_criticas.length > 0 && (
                <>
                  <Typography
                    variant={isMobile ? 'body2' : 'subtitle1'}
                    sx={{ fontWeight: 600, mb: 1.5, color: 'error.main' }}
                  >
                    Críticas (&lt;60% en alguna auditoría)
                  </Typography>
                  <List dense={isMobile} disablePadding sx={{ mb: 2 }}>
                    {data.categorias_criticas.map((cat, index) => (
                      <React.Fragment key={String(cat.id)}>
                        <ListItem sx={{ px: 0, alignItems: 'flex-start', pb: 0.5 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ fontSize: isMobile ? 12 : 14 }}
                                >
                                  {cat.name}
                                </Typography>
                                <Chip
                                  label={`Peor: ${(cat.worst_score ?? cat.average).toFixed(0)}%`}
                                  color="error"
                                  size="small"
                                  sx={{ fontSize: isMobile ? 9 : 11 }}
                                />
                              </Box>
                            }
                            secondary={
                              cat.preguntas_criticas && cat.preguntas_criticas.length > 0 ? (
                                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                  {cat.preguntas_criticas.map((p, pi) => (
                                    <Box
                                      component="li"
                                      key={pi}
                                      sx={{ mb: 0.25 }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: isMobile ? 10 : 12 }}
                                      >
                                        {p.text}{' '}
                                        <Box
                                          component="span"
                                          sx={{ color: 'error.main', fontWeight: 700 }}
                                        >
                                          ({p.average.toFixed(0)}%)
                                        </Box>
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null
                            }
                          />
                        </ListItem>
                        {index < data.categorias_criticas.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}

              {data.categorias_riesgo.length > 0 && (
                <>
                  <Typography
                    variant={isMobile ? 'body2' : 'subtitle1'}
                    sx={{ fontWeight: 600, mb: 1.5, color: 'warning.main' }}
                  >
                    En riesgo (60–79% en alguna auditoría)
                  </Typography>
                  <List dense={isMobile} disablePadding>
                    {data.categorias_riesgo.map((cat, index) => (
                      <React.Fragment key={String(cat.id)}>
                        <ListItem sx={{ px: 0, alignItems: 'flex-start', pb: 0.5 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ fontSize: isMobile ? 12 : 14 }}
                                >
                                  {cat.name}
                                </Typography>
                                <Chip
                                  label={`Peor: ${(cat.worst_score ?? cat.average).toFixed(0)}%`}
                                  color="warning"
                                  size="small"
                                  sx={{ fontSize: isMobile ? 9 : 11 }}
                                />
                              </Box>
                            }
                            secondary={
                              cat.preguntas_criticas && cat.preguntas_criticas.length > 0 ? (
                                <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                  {cat.preguntas_criticas.map((p, pi) => (
                                    <Box component="li" key={pi} sx={{ mb: 0.25 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: isMobile ? 10 : 12 }}
                                      >
                                        {p.text}{' '}
                                        <Box
                                          component="span"
                                          sx={{ color: 'warning.main', fontWeight: 700 }}
                                        >
                                          ({p.average.toFixed(0)}%)
                                        </Box>
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null
                            }
                          />
                        </ListItem>
                        {index < data.categorias_riesgo.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}
            </Grid>
          )}

          {/* Preguntas con Mayor Incidencia */}
          {data.top_preguntas_peores.length > 0 && (
            <Grid item xs={12} md={allCategorias.length > 0 ? 6 : 12}>
              <Typography
                variant={isMobile ? 'body2' : 'subtitle1'}
                sx={{ fontWeight: 600, mb: 1.5, color: 'error.main' }}
              >
                Preguntas con Mayor Incidencia
              </Typography>
              <List dense={isMobile} disablePadding>
                {data.top_preguntas_peores.map((pregunta, index) => (
                  <React.Fragment key={pregunta.id}>
                    <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: isMobile ? 11 : 13, fontWeight: 500 }}
                          >
                            {pregunta.text}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ fontSize: isMobile ? 9 : 11 }}
                            >
                              {pregunta.category}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: isMobile ? 9 : 11,
                                color: `${scoreColor(pregunta.average)}.main`,
                                fontWeight: 700,
                              }}
                            >
                              {pregunta.average.toFixed(1)}%
                              {pregunta.recurrence > 1 && ` • ${pregunta.recurrence} auditorías`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < data.top_preguntas_peores.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default AspectosAMejorar;
